/**
 * Joy palette class maps. Tailwind's JIT only emits utilities it finds as
 * *literal* strings in source, so the dynamic `--color-joy-*` tokens are
 * surfaced here as complete class strings keyed by `JoyKey`. Components look
 * up a key rather than composing class names at runtime (which JIT can't
 * see). These are category-identity colours, not tenant theme roles.
 */

export type JoyKey =
  | "indigo"
  | "sky"
  | "teal"
  | "green"
  | "lime"
  | "amber"
  | "orange"
  | "rose"
  | "pink"
  | "violet";

/** Soft tinted chip: faint background + saturated foreground/icon. */
export const JOY_TINT: Record<JoyKey, string> = {
  indigo: "bg-joy-indigo/12 text-joy-indigo",
  sky: "bg-joy-sky/12 text-joy-sky",
  teal: "bg-joy-teal/14 text-joy-teal",
  green: "bg-joy-green/12 text-joy-green",
  lime: "bg-joy-lime/16 text-joy-lime",
  amber: "bg-joy-amber/16 text-joy-amber",
  orange: "bg-joy-orange/14 text-joy-orange",
  rose: "bg-joy-rose/12 text-joy-rose",
  pink: "bg-joy-pink/12 text-joy-pink",
  violet: "bg-joy-violet/12 text-joy-violet",
};

/** Solid pill: full-saturation background + white glyph. */
export const JOY_SOLID: Record<JoyKey, string> = {
  indigo: "bg-joy-indigo text-white",
  sky: "bg-joy-sky text-white",
  teal: "bg-joy-teal text-white",
  green: "bg-joy-green text-white",
  lime: "bg-joy-lime text-white",
  amber: "bg-joy-amber text-white",
  orange: "bg-joy-orange text-white",
  rose: "bg-joy-rose text-white",
  pink: "bg-joy-pink text-white",
  violet: "bg-joy-violet text-white",
};

/** Progress-bar fill. */
export const JOY_BAR: Record<JoyKey, string> = {
  indigo: "bg-joy-indigo",
  sky: "bg-joy-sky",
  teal: "bg-joy-teal",
  green: "bg-joy-green",
  lime: "bg-joy-lime",
  amber: "bg-joy-amber",
  orange: "bg-joy-orange",
  rose: "bg-joy-rose",
  pink: "bg-joy-pink",
  violet: "bg-joy-violet",
};

/** Text-only colour (no background) — for labels/values keyed by category. */
export const JOY_TEXT: Record<JoyKey, string> = {
  indigo: "text-joy-indigo",
  sky: "text-joy-sky",
  teal: "text-joy-teal",
  green: "text-joy-green",
  lime: "text-joy-lime",
  amber: "text-joy-amber",
  orange: "text-joy-orange",
  rose: "text-joy-rose",
  pink: "text-joy-pink",
  violet: "text-joy-violet",
};

/** Raw CSS var reference for SVG strokes / inline styles. */
export const JOY_VAR: Record<JoyKey, string> = {
  indigo: "var(--color-joy-indigo)",
  sky: "var(--color-joy-sky)",
  teal: "var(--color-joy-teal)",
  green: "var(--color-joy-green)",
  lime: "var(--color-joy-lime)",
  amber: "var(--color-joy-amber)",
  orange: "var(--color-joy-orange)",
  rose: "var(--color-joy-rose)",
  pink: "var(--color-joy-pink)",
  violet: "var(--color-joy-violet)",
};
